import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Clock, Users, Plus, CheckCircle, AlertCircle, CalendarDays, RefreshCw, Trash2, X, Search, Edit, Ban, CalendarX, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { 
  formatInLocalTime, 
  isTodayInLocal, 
  isTomorrowInLocal, 
  isSameDayInLocal, 
  nowInLocal, 
  toApiFormat, 
  fromApiFormat,
  localToUtc 
} from '@/utils/timezone';
import { EventDialog } from '@/components/schedule/EventDialog';
import { TaskDialog } from '@/components/schedule/TaskDialog';
import { CalendarView } from '@/components/schedule/CalendarView';
import { TimezoneClock } from '@/components/ui/timezone-clock';
import { schedulerApi } from '@/services/api/schedulerApi';
import { Event, Task, CreateEventRequest, CreateTaskRequest } from '@/types/scheduler';
import { useAuth } from '@/context/AuthContext';

const Schedule = () => {
  const { toast } = useToast();
  const [events, setEvents] = useState<Event[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [eventSearch, setEventSearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'event' | 'task', id: string, title: string } | null>(null);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [incompleteDialogOpen, setIncompleteDialogOpen] = useState(false);
  const [eventToComplete, setEventToComplete] = useState<{ id: string, title: string } | null>(null);

  // Get user from auth context
  const { user } = useAuth();

  const loadData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const now = nowInLocal();
      const startDate = toApiFormat(startOfWeek(now));
      const endDate = toApiFormat(endOfWeek(now));
      
      const [eventsData, tasksData] = await Promise.all([
        schedulerApi.listEvents({ user_id: user.id }),
        schedulerApi.listTasks()
      ]);
      
      setEvents(Array.isArray(eventsData) ? eventsData : []);
      setTasks(Array.isArray(tasksData) ? tasksData : []);
      
      console.log('🐛 Loaded events:', eventsData?.length || 0, eventsData);
      console.log('🐛 Loaded tasks:', tasksData?.length || 0);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({
        title: "Error",
        description: "Failed to load schedule data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!user) return;
    
    setRefreshing(true);
    try {
      const now = nowInLocal();
      const startDate = toApiFormat(startOfWeek(now));
      const endDate = toApiFormat(endOfWeek(now));
      
      const [eventsData, tasksData] = await Promise.all([
        schedulerApi.listEvents({ user_id: user.id }),
        schedulerApi.listTasks()
      ]);
      
      setEvents(Array.isArray(eventsData) ? eventsData : []);
      setTasks(Array.isArray(tasksData) ? tasksData : []);
      
      toast({
        title: "Success",
        description: "Schedule data refreshed",
      });
    } catch (error) {
      console.error('Failed to refresh data:', error);
      toast({
        title: "Error",
        description: "Failed to refresh schedule data",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  const handleSaveEvent = async (eventData: any) => {
    if (!user) return;
    
    try {
      console.log('🐛 Saving event:', { selectedEvent, eventData });
      
      if (selectedEvent) {
        // Update existing event
        const updateEventRequest = {
          title: eventData.title,
          description: eventData.description,
          start_datetime: eventData.startDate.toISOString().slice(0, 19),
          end_datetime: eventData.endDate.toISOString().slice(0, 19),
          event_type: eventData.eventType || 'meeting',
          priority: eventData.priority || 'medium',
          location: eventData.location,
          related_lead_id: eventData.relatedLeadId || null,
          related_contact_id: eventData.relatedContactId || null,
        };
        
        console.log('🐛 Updating event with payload:', updateEventRequest);
        await schedulerApi.updateEvent(String(selectedEvent.id), updateEventRequest);
        
        toast({
          title: "Success",
          description: "Event updated successfully",
        });
      } else {
        // Create new event
        const createEventRequest: CreateEventRequest = {
          title: eventData.title,
          description: eventData.description,
          start_datetime: eventData.startDate.toISOString().slice(0, 19),
          end_datetime: eventData.endDate.toISOString().slice(0, 19),
          event_type: eventData.eventType || 'meeting',
          priority: eventData.priority || 'medium',
          status: 'scheduled',
          location: eventData.location,
          user_id: user.id,
          created_by: user.id,
          related_lead_id: eventData.relatedLeadId || null,
          related_contact_id: eventData.relatedContactId || null,
        };
        
        console.log('🐛 Creating event with payload:', createEventRequest);
        await schedulerApi.createEvent(createEventRequest);
        
        toast({
          title: "Success",
          description: "Event created successfully",
        });
      }
      
      await loadData();
      setEventDialogOpen(false);
      setSelectedEvent(null);
      setSelectedDate(null);
    } catch (error) {
      console.error('Failed to save event:', error);
      toast({
        title: "Error",
        description: selectedEvent ? "Failed to update event" : "Failed to create event",
        variant: "destructive",
      });
    }
  };

  const handleSaveTask = async (taskData: any) => {
    if (!user) return;
    
    try {
      console.log('🐛 Saving task:', { selectedTask, taskData });
      
      if (selectedTask) {
        // Update existing task
        const updateTaskRequest = {
          title: taskData.title,
          description: taskData.description,
          due_date: taskData.dueDate ? toApiFormat(taskData.dueDate).split('T')[0] : undefined,
          priority: taskData.priority || 'medium',
          estimated_duration: taskData.estimatedDuration && typeof taskData.estimatedDuration === 'number' ? taskData.estimatedDuration : undefined,
        };
        
        console.log('🐛 Updating task with payload:', updateTaskRequest);
        await schedulerApi.updateTask(String(selectedTask.id), updateTaskRequest);
        
        toast({
          title: "Success",
          description: "Task updated successfully",
        });
      } else {
        // Create new task
        const createTaskRequest: CreateTaskRequest = {
          title: taskData.title,
          description: taskData.description,
          due_date: taskData.dueDate ? toApiFormat(taskData.dueDate).split('T')[0] : undefined,
          priority: taskData.priority || 'medium',
          status: 'todo',
          user_id: user.id,
          created_by: user.id,
          estimated_duration: taskData.estimatedDuration && typeof taskData.estimatedDuration === 'number' ? taskData.estimatedDuration : undefined,
        };
        
        console.log('🐛 Creating task with payload:', createTaskRequest);
        await schedulerApi.createTask(createTaskRequest);
        
        toast({
          title: "Success",
          description: "Task created successfully",
        });
      }
      
      await loadData();
      setTaskDialogOpen(false);
      setSelectedTask(null);
    } catch (error) {
      console.error('Failed to save task:', error);
      toast({
        title: "Error",
        description: selectedTask ? "Failed to update task" : "Failed to create task",
        variant: "destructive",
      });
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      await schedulerApi.updateTask(taskId, { status: 'completed' });
      await loadData();
      toast({
        title: "Success",
        description: "Task marked as completed",
      });
    } catch (error) {
      console.error('Failed to complete task:', error);
      toast({
        title: "Error",
        description: "Failed to complete task",
        variant: "destructive",
      });
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await schedulerApi.deleteTask(taskId);
      await loadData();
      toast({
        title: "Success",
        description: "Task deleted successfully",
      });
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast({
        title: "Error",
        description: "Failed to delete task",
        variant: "destructive",
      });
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      await schedulerApi.deleteEvent(eventId);
      await loadData();
      toast({
        title: "Success",
        description: "Event deleted successfully",
      });
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast({
        title: "Error",
        description: "Failed to delete event",
        variant: "destructive",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    if (itemToDelete.type === 'event') {
      await handleDeleteEvent(itemToDelete.id);
    } else {
      await handleDeleteTask(itemToDelete.id);
    }
    
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const openDeleteDialog = (type: 'event' | 'task', id: string, title: string) => {
    setItemToDelete({ type, id, title });
    setDeleteDialogOpen(true);
  };

  const handleCancelEvent = async (eventId: string) => {
    try {
      await schedulerApi.cancelEvent(eventId);
      await loadData();
      toast({
        title: "Success",
        description: "Event cancelled successfully",
      });
    } catch (error) {
      console.error('Failed to cancel event:', error);
      toast({
        title: "Error",
        description: "Failed to cancel event",
        variant: "destructive",
      });
    }
  };

  const handleCompleteEvent = async (eventId: string) => {
    try {
      await schedulerApi.completeEvent(eventId);
      await loadData();
      toast({
        title: "Success",
        description: "Event marked as completed",
      });
    } catch (error) {
      console.error('Failed to complete event:', error);
      toast({
        title: "Error",
        description: "Failed to complete event",
        variant: "destructive",
      });
    }
  };

  const handleIncompleteEvent = async (eventId: string) => {
    try {
      await schedulerApi.incompleteEvent(eventId);
      await loadData();
      toast({
        title: "Success",
        description: "Event marked as incomplete",
      });
    } catch (error) {
      console.error('Failed to mark event as incomplete:', error);
      toast({
        title: "Error",
        description: "Failed to mark event as incomplete",
        variant: "destructive",
      });
    }
  };

  const openCompleteDialog = (eventId: string, title: string) => {
    setEventToComplete({ id: eventId, title });
    setCompleteDialogOpen(true);
  };

  const openIncompleteDialog = (eventId: string, title: string) => {
    setEventToComplete({ id: eventId, title });
    setIncompleteDialogOpen(true);
  };

  const handleCompleteConfirm = async () => {
    if (!eventToComplete) return;
    await handleCompleteEvent(eventToComplete.id);
    setCompleteDialogOpen(false);
    setEventToComplete(null);
  };

  const handleIncompleteConfirm = async () => {
    if (!eventToComplete) return;
    await handleIncompleteEvent(eventToComplete.id);
    setIncompleteDialogOpen(false);
    setEventToComplete(null);
  };

  const getTodaysEvents = () => {
    return events.filter(event => 
      isTodayInLocal(event.start_datetime)
    );
  };

  const getPendingTasks = () => {
    return tasks.filter(task => task.status === 'todo' || task.status === 'pending');
  };

  const getTodaysTasks = () => {
    return tasks.filter(task => 
      task.due_date && isTodayInLocal(task.due_date)
    );
  };

  const getFilteredEvents = () => {
    if (!eventSearch) return events;
    return events.filter(event => 
      event.title.toLowerCase().includes(eventSearch.toLowerCase()) ||
      (event.description && event.description.toLowerCase().includes(eventSearch.toLowerCase())) ||
      (event.location && event.location.toLowerCase().includes(eventSearch.toLowerCase()))
    );
  };

  const getFilteredTasks = () => {
    if (!taskSearch) return tasks;
    return tasks.filter(task => 
      task.title.toLowerCase().includes(taskSearch.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(taskSearch.toLowerCase()))
    );
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Completed</Badge>;
      case 'in_progress':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">In Progress</Badge>;
      case 'todo':
        return <Badge variant="secondary" className="bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">To Do</Badge>;
      case 'pending':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Pending</Badge>;
      case 'scheduled':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Scheduled</Badge>;
      case 'cancelled':
        return <Badge variant="secondary" className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (!user || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start gap-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-muted-foreground" />
          <p className="text-muted-foreground">
            Manage your appointments, meetings, and tasks
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="icon" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" onClick={() => setTaskDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Task
          </Button>
          <Button onClick={() => setEventDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Event
          </Button>
        </div>
      </div>


      <TimezoneClock />

      <div className="grid gap-2 md:grid-cols-3">
        <Card className="p-3">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 p-0">
            <CardTitle className="text-sm font-medium">
              Today's Events
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="p-0 pt-1">
            <div className="text-2xl font-bold">{getTodaysEvents().length}</div>
            <p className="text-xs text-muted-foreground">
              {getTodaysTasks().length} tasks due today
            </p>
          </CardContent>
        </Card>
        <Card className="p-3">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 p-0">
            <CardTitle className="text-sm font-medium">
              Pending Tasks
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="p-0 pt-1">
            <div className="text-2xl font-bold">{getPendingTasks().length}</div>
            <p className="text-xs text-muted-foreground">
              {getTodaysTasks().length} due today
            </p>
          </CardContent>
        </Card>
        <Card className="p-3">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 p-0">
            <CardTitle className="text-sm font-medium">
              This Week
            </CardTitle>
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent className="p-0 pt-1">
            <div className="text-2xl font-bold">{events.length}</div>
            <p className="text-xs text-muted-foreground">
              Events scheduled
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="calendar" className="w-full">
        <TabsList>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
        </TabsList>
        
        <TabsContent value="calendar" className="space-y-4">
          <CalendarView
            events={events}
            tasks={tasks}
            onEventClick={(event) => {
              setSelectedEvent(event);
              setEventDialogOpen(true);
            }}
            onTaskClick={(task) => {
              setSelectedTask(task);
              setTaskDialogOpen(true);
            }}
            onCompleteEvent={openCompleteDialog}
            onIncompleteEvent={openIncompleteDialog}
            onDeleteEvent={(eventId, title) => openDeleteDialog('event', eventId, title)}
            onDateClick={(date) => {
              const dayEvents = events.filter(event => 
                isSameDayInLocal(event.start_datetime, date)
              );
              const dayTasks = tasks.filter(task => 
                task.due_date && isSameDayInLocal(task.due_date, date)
              );
              
              if (dayEvents.length === 0 && dayTasks.length === 0) {
                setSelectedDate(date);
                setEventDialogOpen(true);
              }
            }}
          />
        </TabsContent>
        
        <TabsContent value="events" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6">
              <div>
                <CardTitle>Upcoming Events</CardTitle>
                <CardDescription>
                  Your scheduled events for this week
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2 min-w-0 flex-shrink-0 ml-6">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search events..."
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  className="w-64"
                />
              </div>
            </CardHeader>
            <CardContent>
              {getFilteredEvents().length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {eventSearch ? "No events found matching your search." : "No events scheduled. Create your first event!"}
                </div>
              ) : (
                <div className="space-y-4">
                  {getFilteredEvents().map((event) => {
                    console.log('🐛 Event:', event.id, 'Status:', event.status, 'Title:', event.title);
                    console.log('🐛 Should show complete button:', event.status !== 'completed');
                    return (
                    <div key={event.id} className="flex items-center p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                      <div className={`w-3 h-3 rounded-full mr-3 ${getPriorityColor(event.priority)}`}></div>
                      <div className="flex-1">
                        <h4 className="font-semibold">{event.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {formatInLocalTime(event.start_datetime, 'h:mm a')}
                          {event.end_datetime && ` - ${formatInLocalTime(event.end_datetime, 'h:mm a')}`}
                        </p>
                        {event.description && (
                          <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
                        )}
                        {event.location && (
                          <p className="text-xs text-muted-foreground mt-1">📍 {event.location}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          {getStatusBadge(event.status)}
                          <Badge variant="outline" className={getPriorityColor(event.priority)}>
                            {event.priority}
                          </Badge>
                          <Badge variant="secondary">
                            {event.event_type}
                          </Badge>
                        </div>
                      </div>
                      <TooltipProvider>
                        <div className="flex gap-2">
                          {event.status !== 'completed' ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => openCompleteDialog(String(event.id), event.title)}
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Mark as Complete</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => openIncompleteDialog(String(event.id), event.title)}
                                  className="text-orange-600 hover:text-orange-700"
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Mark as Incomplete</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {event.status === 'scheduled' && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleCancelEvent(String(event.id))}
                                  className="text-orange-600 hover:text-orange-700"
                                >
                                  <Ban className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Cancel Event</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  setSelectedEvent(event);
                                  setEventDialogOpen(true);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Edit Event</p>
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openDeleteDialog('event', String(event.id), event.title)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Delete Event</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </TooltipProvider>
                     </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6">
              <div>
                <CardTitle>Tasks</CardTitle>
                <CardDescription>
                  Your tasks and to-dos
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2 min-w-0 flex-shrink-0 ml-6">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tasks..."
                  value={taskSearch}
                  onChange={(e) => setTaskSearch(e.target.value)}
                  className="w-64"
                />
              </div>
            </CardHeader>
            <CardContent>
              {getFilteredTasks().length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {taskSearch ? "No tasks found matching your search." : "No tasks found. Create your first task!"}
                </div>
              ) : (
                <div className="space-y-4">
                  {getFilteredTasks().map((task) => (
                    <div key={task.id} className="flex items-center p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                      <div className={`w-3 h-3 rounded-full mr-3 ${getPriorityColor(task.priority)}`}></div>
                      <div className="flex-1">
                        <h4 className="font-semibold">{task.title}</h4>
                        {task.description && (
                          <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                        )}
                        {task.due_date && (
                          <p className="text-sm text-muted-foreground">
                            Due: {formatInLocalTime(task.due_date, 'MMM d, yyyy h:mm a')}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          {getStatusBadge(task.status)}
                          <Badge variant="outline" className={getPriorityColor(task.priority)}>
                            {task.priority}
                          </Badge>
                        </div>
                      </div>
                        <TooltipProvider>
                          <div className="flex gap-2">
                            {(task.status === 'todo' || task.status === 'pending') && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleCompleteTask(String(task.id))}
                                    className="text-green-600 hover:text-green-700"
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Mark as Complete</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => {
                                    setSelectedTask(task);
                                    setTaskDialogOpen(true);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Edit Task</p>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => openDeleteDialog('task', String(task.id), task.title)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Delete Task</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TooltipProvider>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <EventDialog
        open={eventDialogOpen}
        onOpenChange={(open) => {
          setEventDialogOpen(open);
          if (!open) {
            setSelectedEvent(null);
            setSelectedDate(null);
          }
        }}
        event={selectedEvent}
        selectedDate={selectedDate}
        onSave={handleSaveEvent}
      />

      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={(open) => {
          setTaskDialogOpen(open);
          if (!open) setSelectedTask(null);
        }}
        task={selectedTask}
        onSave={handleSaveTask}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the {itemToDelete?.type} "{itemToDelete?.title}". 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Event as Complete</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark "{eventToComplete?.title}" as completed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCompleteConfirm}
              className="bg-green-600 hover:bg-green-700"
            >
              Mark Complete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={incompleteDialogOpen} onOpenChange={setIncompleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Event as Incomplete</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark "{eventToComplete?.title}" as incomplete?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleIncompleteConfirm}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Mark Incomplete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Schedule;